import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export function useRealtimeUpdates(refreshData: () => void) {
  const { user } = useAuth();
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (!user) return;

    // Create realtime channel
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities'
        },
        (payload) => {
          try {
            if (payload.new?.user_id !== user.id) {
              toast.success(`${payload.new?.name || 'Activity'} logged!`, {
                description: `Someone just earned ${payload.new?.points || 0} points`
              });
              refreshData();
            } else {
              // Skip immediate refresh for own inserts to avoid clobbering local pending saves
              setTimeout(() => {
                try {
                  refreshData();
                } catch (e) {
                  console.error('Error during delayed refresh:', e);
                }
              }, 1500);
            }
          } catch (error) {
            console.error('Error handling activity insert:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'virtual_pets'
        },
        (payload) => {
          try {
            if (payload.new?.user_id !== user.id) {
              toast.info('A pet has been updated!', {
                description: `${payload.new?.name || 'Pet'} is feeling different`
              });
            }
            refreshData();
          } catch (error) {
            console.error('Error handling pet update:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_comments'
        },
        (payload) => {
          try {
            if (payload.new?.user_id !== user.id) {
              toast.info('New comment on activity!', {
                description: 'Someone commented on an activity'
              });
            }
            refreshData();
          } catch (error) {
            console.error('Error handling comment insert:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pet_gifts'
        },
        (payload) => {
          try {
            if (payload.new?.receiver_id === user.id) {
              toast.success('🎁 You received a pet gift!', {
                description: 'Check your pet gifts to claim it'
              });
            }
            refreshData();
          } catch (error) {
            console.error('Error handling pet gift:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'challenges'
        },
        (payload) => {
          try {
            if (payload.new?.participants?.includes(user.id)) {
              toast.info('Challenge updated!', {
                description: payload.new?.title || 'Challenge'
              });
            }
            refreshData();
          } catch (error) {
            console.error('Error handling challenge update:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'friendships'
        },
        (payload) => {
          try {
            if (payload.new?.friend_id === user.id) {
              toast.info('🤝 New friend request!', {
                description: 'Someone wants to be your friend'
              });
            }
            refreshData();
          } catch (error) {
            console.error('Error handling friendship insert:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'friendships'
        },
        (payload) => {
          try {
            if (payload.new?.user_id === user.id || payload.new?.friend_id === user.id) {
              if (payload.new?.status === 'accepted') {
                toast.success('🎉 Friend request accepted!', {
                  description: 'You have a new friend'
                });
              }
            }
            refreshData();
          } catch (error) {
            console.error('Error handling friendship update:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_challenges'
        },
        (payload) => {
          try {
            toast.info('🏆 New team challenge available!', {
              description: payload.new?.title || 'New challenge'
            });
            refreshData();
          } catch (error) {
            console.error('Error handling team challenge insert:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events'
        },
        (payload) => {
          try {
            const eventType = payload.eventType;
            if (eventType === 'INSERT') {
              toast.info('📅 New event created!', {
                description: payload.new?.title || 'Event'
              });
              // Add a delay for INSERT to allow trigger to complete
              setTimeout(() => refreshData(), 1000);
            } else if (eventType === 'UPDATE') {
              toast.info('📅 Event updated!', {
                description: payload.new?.title || 'Event'
              });
              refreshData();
            } else {
              // DELETE or other events
              refreshData();
            }
          } catch (error) {
            console.error('Error handling event change:', error);
          }
        }
      )
      // Habits real-time updates disabled to prevent race conditions with local state
      // .on(
      //   'postgres_changes',
      //   {
      //     event: '*',
      //     schema: 'public',
      //     table: 'habits'
      //   },
      //   (payload) => {
      //     try {
      //       const newUserId = (payload.new as any)?.user_id;
      //       const oldUserId = (payload.old as any)?.user_id;
      //       if (newUserId === user.id || oldUserId === user.id) {
      //         // Add a small delay to prevent race conditions with habit creation
      //         setTimeout(() => refreshData(), 1000);
      //       }
      //     } catch (error) {
      //       console.error('Error handling habit change:', error);
      //     }
      //   }
      // )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user, refreshData]);

  return { isConnected: channelRef.current?.state === 'joined' };
}